import { Button, Spinner } from '@monksflow/monks-ui';
import { DownloadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AutoAwesomeIcon, UploadFileIcon } from '@/shared/ui/Icons';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import { useProfileImageSection } from './hooks/useProfileImageSection';
import type { ProfileImageSectionProps } from './ProfileImageSection.types';

const FILL_CENTERED = 'flex size-full items-center justify-center';
const METADATA_ENTRY = 'flex flex-col gap-0.5';

export function ProfileImageSection({
  photoAssetId,
  isProcessing,
  processingLabel,
  disabled,
  onUploadClick,
  onRegenerateClick,
  metadata,
}: ProfileImageSectionProps) {
  const { t } = useTranslation('audiences');

  const { downloadUrl, isLoadingUrl, isHovered, setIsHovered, handleDownload } =
    useProfileImageSection(photoAssetId);

  const showLoadingSpinner = photoAssetId && isLoadingUrl;
  const showImage = downloadUrl && !isLoadingUrl;
  const showEmptyState = !photoAssetId && !isProcessing;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{t('audiences.profileImageSection.profileImage')}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onUploadClick} disabled={disabled ?? isProcessing}>
            <UploadFileIcon className="size-5" />
            {t('audiences.profileImageSection.uploadFile')}
          </Button>
          <Button variant="outline" onClick={onRegenerateClick} disabled={disabled ?? isProcessing}>
            <AutoAwesomeIcon className="size-5" />
            {t('audiences.profileImageSection.reGenerateImage')}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div
          className={`relative size-40 shrink-0 overflow-hidden rounded-2xl border border-border ${
            showEmptyState ? 'bg-muted' : 'bg-transparent'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {showEmptyState && (
            <div className={FILL_CENTERED}>
              <p className="text-sm text-muted-foreground">
                {t('audiences.profileImageSection.noImage')}
              </p>
            </div>
          )}

          {showLoadingSpinner && (
            <div className={FILL_CENTERED}>
              <Spinner size="lg" />
            </div>
          )}

          {showImage && (
            <OptimizedImage
              src={downloadUrl}
              alt={t('audiences.profileImageSection.profileImage')}
              priority
              showSkeleton
              containerClassName="size-full"
              className="size-full object-cover"
            />
          )}

          {isProcessing && (
            <div className={`absolute inset-0 flex-col bg-background/80 ${FILL_CENTERED}`}>
              <Spinner size="lg" />
              {processingLabel && (
                <p className="mt-2 text-xs text-muted-foreground">{processingLabel}</p>
              )}
            </div>
          )}

          {showImage && isHovered && (
            <Button
              variant="secondary"
              size="icon-sm"
              className="absolute top-2 right-2"
              aria-label={t('audiences.profileImageSection.downloadImage')}
              onClick={handleDownload}
            >
              <DownloadIcon />
            </Button>
          )}
        </div>

        {metadata && (
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {metadata.source === 'agent' && metadata.promptUsed && (
              <div className={METADATA_ENTRY}>
                <p className="text-xs text-muted-foreground">
                  {t('audiences.profileImageSection.promptUsed')}
                </p>
                <p className="text-sm">{metadata.promptUsed}</p>
              </div>
            )}
            <div className="flex gap-8">
              <div className={METADATA_ENTRY}>
                <p className="text-xs text-muted-foreground">
                  {metadata.source === 'agent'
                    ? t('audiences.profileImageSection.createdAt')
                    : t('audiences.profileImageSection.uploadedAt')}
                </p>
                <p className="text-sm">{metadata.createdAt ?? '-'}</p>
              </div>
              {metadata.source === 'agent' && metadata.modelUsed && (
                <div className={METADATA_ENTRY}>
                  <p className="text-xs text-muted-foreground">
                    {t('audiences.profileImageSection.modelUsed')}
                  </p>
                  <p className="text-sm">{metadata.modelUsed}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
